<template>
    <form-wrapper @form-submit="checkPersonalLocationData">
        <form-row v-if="!showAddCredentialsButton">
            <form-cell width="half" class="credential-buttons--parent">
                <text-input
                    :formData="data"
                    :label="$t('forms.user.profile.fields.email.label')"
                    :placeholder="$t('forms.user.profile.fields.email.placeholder')"
                    :isRequired="true"
                    :isDisabled="true"
                    type="email"
                    valuePath="email"
                    @change="change"
                ></text-input>
                <div class="credential-buttons--label" @click="openModal('ChangeEmail')">
                    {{ $t('forms.user.profile.buttons.change') }}
                </div>
            </form-cell>
            <form-cell width="half" class="credential-buttons-form-cell">
                <common-buttons class="credential-buttons">
                    <common-button
                        v-if="showChangeEmailButton"
                        type="link"
                        :isSubmittable="false"
                        :label="getEmailLabel"
                        @click="openModal('ChangeEmail')"
                    ></common-button>
                    <common-button
                        v-if="showAddPasswordButton"
                        type="link"
                        :isSubmittable="false"
                        :label="$t('forms.user.profile.buttons.add_password')"
                        @click="openModal('AddPassword')"
                    ></common-button>
                    <common-button
                        v-if="showChangePasswordButton"
                        type="link"
                        :isSubmittable="false"
                        :label="$t('forms.user.profile.buttons.change_password')"
                        @click="openModal('ChangePassword')"
                    ></common-button>
                </common-buttons>
            </form-cell>
        </form-row>
        <form-row v-if="showAddCredentialsButton">
            <form-cell>
                <common-buttons>
                    <common-button
                        type="link"
                        :isSubmittable="false"
                        :label="$t('forms.user.profile.buttons.add_credentials')"
                        @click="openModal('AddCredentials')"
                    ></common-button>
                </common-buttons>
            </form-cell>
        </form-row>

        <form-row>
            <form-cell width="half" class="credential-buttons--parent">
                <text-input
                    :formData="data"
                    :label="$t('forms.user.profile.fields.phone.label')"
                    :placeholder="$t('forms.user.profile.fields.phone.placeholder')"
                    :isRequired="true"
                    :isDisabled="true"
                    :error="data.mobile ? undefined : ' '"
                    type="text"
                    valuePath="mobile"
                    @change="change"
                ></text-input>
                <div class="credential-buttons--label" @click="openModal('ChangePhone')">
                    {{ $t('forms.user.profile.buttons.change') }}
                </div>
            </form-cell>
            <form-cell width="half" class="credential-buttons-form-cell">
                <common-buttons class="credential-buttons">
                    <common-button
                        type="link"
                        :isSubmittable="false"
                        :label="getPhoneLabel"
                        @click="openModal('ChangePhone')"
                    ></common-button>
                    <common-button
                        type="link"
                        :isSubmittable="false"
                        :isDisabled="!$auth.getUser()?.pendingMobile"
                        :label="$t('forms.user.profile.buttons.verify_phone')"
                        @click="openModal('VerifyPhone')"
                    ></common-button>
                </common-buttons>
            </form-cell>
        </form-row>

        <template #footer>
            <common-buttons>
                <common-button
                    v-if="!userLocations.length"
                    type="primary"
                    :isProcessing="isLoading"
                    :label="$t('forms.user.profile.buttons.go_to_location')"
                ></common-button>
            </common-buttons>
            <change-password-modal
                v-if="isChangePasswordModalOpen"
                @close="closeModal('ChangePassword')"
                @success="modalFormSubmittedSuccess('ChangePassword')"
            ></change-password-modal>

            <change-email-modal
                v-if="isChangeEmailModalOpen"
                @close="closeModal('ChangeEmail')"
                @success="modalFormSubmittedSuccess('ChangeEmail')"
            ></change-email-modal>

            <change-phone-modal
                v-if="isChangePhoneModalOpen"
                @close="closeModal('ChangePhone')"
                @success="modalFormSubmittedSuccess('ChangePhone')"
            ></change-phone-modal>

            <verify-phone-modal
                v-if="isVerifyPhoneModalOpen"
                :isSmsSent="isSmsSent"
                @close="closeModal('VerifyPhone')"
                @success="modalFormSubmittedSuccess('VerifyPhone')"
                @sms-countdown-over="isSmsSent = false"
            ></verify-phone-modal>

            <add-credentials-modal
                v-if="isAddCredentialsModalOpen"
                @close="closeModal('AddCredentials')"
                @success="modalFormSubmittedSuccess('AddCredentials')"
            ></add-credentials-modal>

            <add-password-modal
                v-if="isAddPasswordModalOpen"
                @close="closeModal('AddPassword')"
                @success="modalFormSubmittedSuccess('AddPassword')"
            ></add-password-modal>
        </template>
    </form-wrapper>
</template>

<script>
    import FormMixin from 'mixins/FormMixin'
    import ChangePasswordModal from 'components/modals/user/ChangePasswordModal'
    import ChangeEmailModal from 'components/modals/user/ChangeEmailModal'
    import ChangePhoneModal from 'components/modals/user/ChangePhoneModal'
    import VerifyPhoneModal from 'components/modals/user/VerifyPhoneModal'
    import AddCredentialsModal from 'components/modals/user/AddCredentialsModal'
    import AddPasswordModal from 'components/modals/user/AddPasswordModal'

    export default {
        mixins: [FormMixin],
        components: {
            ChangePasswordModal,
            ChangeEmailModal,
            AddCredentialsModal,
            AddPasswordModal,
            ChangePhoneModal,
            VerifyPhoneModal,
        },

        props: {
            userLocations: { type: Array, required: true },
        },

        data() {
            return {
                isChangePasswordModalOpen: false,
                isChangeEmailModalOpen: false,
                isChangePhoneModalOpen: false,
                isVerifyPhoneModalOpen: false,
                isAddCredentialsModalOpen: false,
                isAddPasswordModalOpen: false,
                user: null,
                yesNoHeaderText: '',
                isSmsSent: false,
            }
        },

        computed: {
            showChangePasswordButton() {
                return this.user?.password
            },
            showChangeEmailButton() {
                return this.user?.password && this.user?.email
            },

            showAddPasswordButton() {
                return !this.user?.password && this.user?.email
            },

            showAddCredentialsButton() {
                return !this.user?.password && !this.user?.email
            },

            getPhoneLabel() {
                return this.user?.phone?.length
                    ? this.$t('forms.user.profile.buttons.change_phone')
                    : this.$t('forms.user.profile.buttons.enter_phone')
            },

            getEmailLabel() {
                return this.user?.email?.length
                    ? this.$t('forms.user.profile.buttons.change_email')
                    : this.$t('forms.user.profile.buttons.enter_email')
            },
        },

        methods: {
            openModal(modal) {
                this['is' + modal + 'ModalOpen'] = true
            },

            closeModal(modal) {
                this['is' + modal + 'ModalOpen'] = false
            },

            checkPersonalLocationData() {
                this.emitEvent({ name: 'open-location-tab' })
            },

            modalFormSubmittedSuccess(modal) {
                if (modal === 'ChangeEmail' || modal === 'AddCredentials') {
                    this.$toast.success(this.$t('forms.user.profile.messages.confirm_email'))
                } else if (modal === 'ChangePhone') {
                    if (process.env.VUE_APP_ENV !== 'prod' && process.env.VUE_APP_ENV !== 'stage') {
                        this.isSmsSent = true
                        this.openModal('VerifyPhone')
                    }
                } else {
                    this.$toast.success(this.$t('common.microcopy.success'))
                }

                this.$auth.reloadUser().then(() => {
                    this.user = this.$auth.getUser()
                    this.prefillData(this.user)
                })
                this.closeModal(modal)
            },
        },

        mounted() {
            this.user = this.$auth.getUser()
            this.prefillData(this.user)
        },
    }
</script>

<style lang="scss" scoped>
    @import 'styles/setup/_variables.scss';
    @import 'styles/setup/mixins';

    .credential-buttons-form-cell {
        align-self: flex-end;
    }
    .credential-buttons {
        height: 100%;
        display: flex;
        flex-direction: column;
        button {
            margin: 2px 0;
        }

        &--parent {
            position: relative;
        }

        &--label {
            text-align: right;
            align-items: flex-end;
            min-width: 70px;
            position: absolute;
            top: 38px;
            right: 20px;
            color: $color-link;
            cursor: pointer;
            backdrop-filter: blur(30px);
        }
    }

    .subdomain-layout {
        &--navy_black,
        &--black {
            .credential-buttons {
                &--label {
                    color: $subdomain-blue;
                }
            }
        }
    }
</style>
